import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { stepFields } from "views/admin/companies/components/utils/buildPayload";

export function useStepNavigation(
  step: number,
  setStep: (step: number) => void,
  isViewMode: boolean
) {
  const { t } = useTranslation();
  const { trigger, formState, getFieldState, watch, setError, clearErrors } =
    useFormContext();
  const [isErrorsBoxOpen, setIsErrorsBoxOpen] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const errorRef = useRef<HTMLDivElement>(null);

  const suppliers = watch("suppliers");
  const customers = watch("customers");
  const processSteps = watch("processSteps");

  /**
   * Validates array fields on step 3
   */
  const validateArrays = useCallback((): boolean => {
    if (step !== 3) {
      clearErrors("suppliers");
      clearErrors("customers");
      clearErrors("processSteps");
      return true;
    }

    let isValid = true;

    // suppliers
    if (!suppliers || suppliers.length === 0) {
      setError("suppliers", {
        type: "minItems",
        message: t("validation.minItems", {
          count: 1,
          field: t("company.fields.suppliers"),
        }),
      });
      isValid = false;
    } else if (
      suppliers.some(
        (i: any) => !i.supplierName?.trim() || !i.supplierDescription?.trim()
      )
    ) {
      setError("suppliers", {
        type: "incomplete",
        message: t("validation.completeAllItems", {
          field: t("company.fields.suppliers"),
        }),
      });
      isValid = false;
    } else {
      clearErrors("suppliers");
    }

    // customers
    if (!customers || customers.length === 0) {
      setError("customers", {
        type: "minItems",
        message: t("validation.minItems", {
          count: 1,
          field: t("company.fields.customers"),
        }),
      });
      isValid = false;
    } else if (
      customers.some(
        (i: any) => !i.customerName?.trim() || !i.customerDescription?.trim()
      )
    ) {
      setError("customers", {
        type: "incomplete",
        message: t("validation.completeAllItems", {
          field: t("company.fields.customers"),
        }),
      });
      isValid = false;
    } else {
      clearErrors("customers");
    }

    // processSteps
    if (!processSteps || processSteps.length < 3) {
      setError("processSteps", {
        type: "minItems",
        message: t("validation.minItems", {
          count: 3,
          field: t("company.processSteps.title"),
        }),
      });
      isValid = false;
    } else {
      const hasEmptyStep = processSteps.some((s: any) => {
        const hasImage = Array.isArray(s?.image)
          ? s.image.length > 0
          : !!s?.image;
        return !s?.title?.trim() || !s?.description?.trim() || !hasImage;
      });
      if (hasEmptyStep) {
        setError("processSteps", {
          type: "incomplete",
          message: t("validation.completeAllItems", {
            field: t("company.processSteps.title"),
          }),
        });
        isValid = false;
      } else {
        clearErrors("processSteps");
      }
    }

    return isValid;
  }, [step, suppliers, customers, processSteps, setError, clearErrors, t]);

  /**
   * Collects all error messages for the current step
   */
  const currentStepErrors = useMemo(() => {
    if (isViewMode) return [];

    const errors: string[] = [];

    const currentStepFields = stepFields[step as keyof typeof stepFields];
    if (currentStepFields) {
      currentStepFields.forEach((field) => {
        const fieldState = getFieldState(field as any, formState);
        if (fieldState.error?.message) {
          errors.push(fieldState.error.message);
        }
      });
    }

    if (step === 3) {
      const suppliersError = formState.errors.suppliers;
      if (
        suppliersError?.message &&
        typeof suppliersError.message === "string"
      ) {
        errors.push(suppliersError.message);
      }

      const customersError = formState.errors.customers;
      if (
        customersError?.message &&
        typeof customersError.message === "string"
      ) {
        errors.push(customersError.message);
      }

      const processStepsError = formState.errors.processSteps;
      if (
        processStepsError?.message &&
        typeof processStepsError.message === "string"
      ) {
        errors.push(processStepsError.message);
      }
    }

    return [...new Set(errors)];
  }, [isViewMode, step, getFieldState, formState]);

  const errorCount = currentStepErrors.length;

  /**
   * Scrolls to the error box container
   */
  const scrollToErrorBox = useCallback(() => {
    const errorBoxElement = document.getElementById("error-box-container");
    if (errorBoxElement) {
      errorBoxElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return true;
    }
    return false;
  }, []);

  /**
   * Handles navigation to the next step with validation
   */
  const nextStep = useCallback(async () => {
    if (isViewMode) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const fieldsToValidate = stepFields[step as keyof typeof stepFields];
    let isValid = true;

    if (fieldsToValidate?.length) {
      isValid = await trigger(fieldsToValidate as any);
    }

    if (step === 3) {
      const arraysValid = validateArrays();
      if (!arraysValid) isValid = false;
    }

    if (!isValid) {
      setIsErrorsBoxOpen(true);
      setTimeout(() => scrollToErrorBox(), 150);
      return;
    }

    setCompletedSteps((prev) => new Set([...prev, step]));
    setIsErrorsBoxOpen(false);
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, trigger, setStep, isViewMode, validateArrays, scrollToErrorBox]);

  /**
   * Handles navigation to the previous step
   */
  const prevStep = useCallback(() => {
    setIsErrorsBoxOpen(false);
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setStep, step]);

  /**
   * Auto-open error box when errors exist on step change
   */
  useEffect(() => {
    if (errorCount === 0) {
      setIsErrorsBoxOpen(false);
    } else {
      setIsErrorsBoxOpen(true);
    }
  }, [step, errorCount]);

  return {
    isErrorsBoxOpen,
    setIsErrorsBoxOpen,
    completedSteps,
    errorRef,
    currentStepErrors,
    errorCount,
    nextStep,
    prevStep,
    scrollToErrorBox,
  };
}
